import DOMPurify from 'dompurify';
import { ArrowRight, CalendarDays, MapPin, Trophy, Users } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { unwrapApiArray } from '../../shared/utils/apiResponse';
import { userSilentApi } from '../../utils/apiInstances';
import { formatCurrency } from '../../utils/currency';
import LoadingGrid from '../../utils/loadingGrid';
import PitchImagesCarousel from '../../utils/pitchImageCarousel';

interface MediaItem {
    url?: string;
    [key: string]: unknown;
}

interface Tournament {
    id: string | number;
    title: string;
    excerpt?: string;
    start_date?: string;
    entry_fee?: number | string | null;
    prize_pool?: number | string | null;
    max_teams?: number | null;
    registered_teams_count?: number;
    spots_remaining?: number | null;
    team_size?: number | null;
    registration_deadline?: string | null;
    registration_open?: boolean;
    is_full?: boolean;
    currency?: string;
    country_code?: string;
    state?: string;
    city?: string;
    media?: MediaItem[] | string;
}

const parseMedia = (media: Tournament['media']): MediaItem[] => {
    if (Array.isArray(media)) return media;
    if (!media) return [];

    try {
        const parsed = JSON.parse(media);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const TournamentsLanding: React.FC = () => {
    const [tournaments, setTournaments] = useState<Tournament[]>([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        let active = true;

        const fetchTournaments = async () => {
            try {
                const response = await userSilentApi('GET', '/user/leagues?type=tournament&status=Upcoming');
                if (active) setTournaments(unwrapApiArray<Tournament>(response));
            } catch (error) {
                console.error('Error fetching tournaments:', error);
                if (active) setFailed(true);
            } finally {
                if (active) setLoading(false);
            }
        };

        fetchTournaments();
        return () => { active = false; };
    }, []);

    const openTournament = (tournament: Tournament) => {
        const destination = `/dashboard/competitions/${tournament.id}?type=tournament`;
        if (localStorage.getItem('saca_token')) {
            navigate(destination);
            return;
        }
        navigate(`/sign-in?redirect=${encodeURIComponent(destination)}`);
    };

    return (
        <div id="tournaments-section" className="w-full py-4 md:py-8 scroll-mt-28">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                <div>
                    <p className="text-xs font-black uppercase tracking-[0.22em] text-green-700">Team Competition</p>
                    <h2 className="mt-2 text-3xl md:text-5xl font-black text-slate-950">Upcoming tournaments</h2>
                    <p className="mt-3 text-slate-600 max-w-2xl">Register a squad, secure your place, invite teammates, and follow fixtures from one dashboard.</p>
                </div>
                <div className="hidden md:flex w-16 h-16 rounded-2xl bg-amber-400 items-center justify-center text-slate-950 rotate-3">
                    <Trophy size={30} />
                </div>
            </div>

            {loading ? (
                <LoadingGrid count={3} />
            ) : failed ? (
                <EmptyState title="Tournaments could not be loaded" detail="Please refresh the page and try again." />
            ) : tournaments.length === 0 ? (
                <EmptyState title="No tournaments are open right now" detail="New competitions will appear here as soon as registration opens." />
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {tournaments.map((item) => {
                        const location = [item.city, item.state, item.country_code].filter(Boolean).join(', ') || 'Location to be announced';
                        const fee = Number(item.entry_fee || 0) > 0
                            ? formatCurrency(item.entry_fee, { currencyCode: item.currency, countryCode: item.country_code })
                            : 'Free entry';
                        const teamCount = item.registered_teams_count ?? 0;
                        const closed = item.is_full || item.registration_open === false;

                        return (
                            <article key={item.id} className="group overflow-hidden rounded-[28px] border border-stone-200 bg-[#F4F1E9] shadow-sm hover:shadow-xl transition-shadow">
                                <div className="h-56 overflow-hidden bg-slate-200">
                                    <PitchImagesCarousel showIndicator={false} media={parseMedia(item.media)} />
                                </div>
                                <div className="p-6 md:p-7">
                                    <div className="flex items-start justify-between gap-4">
                                        <h3 className="text-xl md:text-2xl font-black text-slate-950 leading-tight">{item.title}</h3>
                                        <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${closed ? 'bg-slate-200 text-slate-600' : 'bg-green-700 text-white'}`}>
                                            {item.is_full ? 'Full' : closed ? 'Closed' : 'Open'}
                                        </span>
                                    </div>

                                    {item.excerpt && (
                                        <div className="mt-3 text-sm text-slate-600 line-clamp-2" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.excerpt) }} />
                                    )}

                                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-700">
                                        <Meta icon={MapPin} text={location} />
                                        <Meta icon={CalendarDays} text={formatTournamentDate(item.start_date)} />
                                        <Meta icon={Users} text={item.max_teams ? `${teamCount}/${item.max_teams} teams` : `${teamCount} teams`} />
                                        <Meta icon={Trophy} text={fee} />
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => openTournament(item)}
                                        className="mt-6 inline-flex items-center gap-2 rounded-xl bg-green-800 px-5 py-3 text-sm font-bold text-white hover:bg-green-900 transition-colors"
                                    >
                                        {closed ? 'View Tournament' : 'View & Register'} <ArrowRight size={16} />
                                    </button>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}

            {tournaments.length > 2 && (
                <Link to="/dashboard/competitions" className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-green-800 hover:text-green-950">
                    View all competitions <ArrowRight size={16} />
                </Link>
            )}
        </div>
    );
};

const Meta: React.FC<{ icon: React.ElementType; text: string }> = ({ icon: Icon, text }) => (
    <div className="flex items-center gap-2 min-w-0"><Icon size={16} className="text-green-700 shrink-0" /><span className="truncate">{text}</span></div>
);

const EmptyState: React.FC<{ title: string; detail: string }> = ({ title, detail }) => (
    <div className="rounded-[24px] border border-dashed border-stone-300 bg-stone-50 px-6 py-14 text-center">
        <Trophy size={36} className="mx-auto text-stone-300" />
        <h3 className="mt-4 text-lg font-bold text-slate-900">{title}</h3>
        <p className="mt-1 text-sm text-slate-500">{detail}</p>
    </div>
);

const formatTournamentDate = (value?: string) => {
    if (!value) return 'Date to be announced';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export default TournamentsLanding;
